// Printed table rows and search notes for the engineer's Fault Assistant (Session BBX3).
//
// A result that came from a manual's own fault code table or troubleshooting table shows the ROWS exactly as the manual
// prints them: the symptom (or the code), every possible cause in the order printed, and the remedy. Nothing is reworded,
// summarised or reordered, and no cause is ever shown that is not a printed line. A remedy with a safety step is marked and
// tells the reader to read the page itself.
//
// The notes say what was changed for the person: "Showing results for ..." when a word was spelt differently, and
// "Also searched: ..." when a reviewed phrase added the manual's own plain terms to the match. Plain words, no numbers.
import type { KnowledgeFaultRow, KnowledgeSpellingCorrection } from "../../lib/knowledgeTypes";

export function PrintedRows({ rows }: { rows: KnowledgeFaultRow[] | null | undefined }) {
  if (!rows || rows.length === 0) return null;
  return (
    <span className="mt-2 block rounded border border-ele-purple-accent p-2" data-testid="printed-rows">
      <span className="block font-menu text-[11px] uppercase tracking-wide text-ele-body">Printed in the manual</span>
      {rows.map((r) => (
        <span key={r.id} className="mt-2 block text-sm text-ele-strong">
          <span className="block break-words font-heading text-sm">
            {r.kind === "code" ? [r.code, r.meaning].filter(Boolean).join("  ") : r.symptom}
          </span>
          {r.causeLines.length > 0 && (
            <span className="mt-1 block">
              <span className="block font-menu text-[11px] uppercase tracking-wide text-ele-body">
                {r.causeLines.length === 1 ? "Possible cause" : "Possible causes, in the order printed"}
              </span>
              {r.causeLines.map((c, i) => (
                <span key={i} className="block break-words text-sm text-ele-body">
                  {`${i + 1}. ${c}`}
                </span>
              ))}
            </span>
          )}
          {r.remedy && (
            <span className="mt-1 block">
              <span className="block font-menu text-[11px] uppercase tracking-wide text-ele-body">Remedy</span>
              <span className="block whitespace-pre-wrap break-words text-sm text-ele-body">{r.remedy}</span>
            </span>
          )}
          {r.safety && (
            <span className="mt-1 block text-xs text-ele-strong">
              This remedy includes a safety step. Read the page itself, as printed, before you act.
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

export function SearchNotes({ correction, alsoSearched }: { correction?: KnowledgeSpellingCorrection | null; alsoSearched?: string | null }) {
  if (!correction && !alsoSearched) return null;
  return (
    <div className="mt-2 text-sm text-ele-body" data-testid="search-notes">
      {correction && <p>{correction.notice}</p>}
      {alsoSearched && <p>{alsoSearched}</p>}
    </div>
  );
}
